import axios from "axios";
import { url } from "../config";
import { clearCachedAccount } from "../../utils/account";

export const api = axios.create({
  baseURL: url,
  timeout: 60000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("remnutri_auth_token");
    if (token) {
      config.headers.Authorization = `${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const LOGIN_PATH = "/login";

// Clearing storage and doing a full-page redirect (rather than dispatching
// LOGOUT) keeps this file free of a store import — store -> root-saga -> sagas
// -> api would be a circular dependency. The reload resets redux state anyway.
const forceReauthentication = () => {
  localStorage.removeItem("remnutri_auth_token");
  localStorage.removeItem("remnutri_access");
  localStorage.removeItem("remnutri_module_grants");
  localStorage.removeItem("setup");
  clearCachedAccount();

  if (window.location.pathname !== LOGIN_PATH) {
    window.location.assign(LOGIN_PATH);
  }
};

// This API does not use 401 for an expired session. A missing token returns
// HTTP 400 "Token is required" and a stale or malformed one returns HTTP 400
// "Invalid or Expried Token" (the server's spelling). Matching only on 401
// meant an expired session never sent the user back to login — every screen
// just showed an error until they logged out by hand.
const AUTH_FAILURE_MESSAGES = ["token is required", "invalid or expried token"];

const isAuthFailure = (error: any) => {
  if (error?.response?.status === 401) return true;
  if (error?.response?.status !== 400) return false;

  const message = String(error?.response?.data?.message ?? "").toLowerCase();
  return AUTH_FAILURE_MESSAGES.some((known) => message.includes(known));
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A failure on the login request itself is "wrong credentials", not an
    // expired session — let the login screen show its own error instead of
    // redirecting away from it.
    const isLoginRequest = Boolean(error?.config?.url?.includes("/auth/user/login"));

    if (isAuthFailure(error) && !isLoginRequest) {
      forceReauthentication();
    }

    return Promise.reject(error);
  }
);
